import * as Tooltip from '@radix-ui/react-tooltip';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

interface HelpHintProps {
    /** Texto explicativo (usar HELP_TEXT de lib/helpText). */
    text: string;
    /** aria-label del botón (default "Ayuda"). */
    label?: string;
    className?: string;
    /** Lado preferido del tooltip. */
    side?: 'top' | 'right' | 'bottom' | 'left';
    /** Dentro de un `<fieldset disabled>` un `<button>` se apaga; el `span` sigue dando la ayuda. */
    asSpan?: boolean;
}

/**
 * Icono de ayuda (?) con tooltip. Orienta al usuario explicando qué significa un
 * concepto/campo. Accesible (botón enfocable + aria-label + rol de tooltip de Radix).
 * Uso: <HelpHint text={HELP_TEXT.tenant} />
 */
export function HelpHint({
    text,
    label = 'Ayuda',
    className,
    side = 'top',
    asSpan = false,
}: HelpHintProps) {
    const triggerClass = cn(
        'inline-flex shrink-0 items-center justify-center rounded-full text-muted-foreground/70 transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
    );
    return (
        <Tooltip.Provider delayDuration={150}>
            <Tooltip.Root>
                <Tooltip.Trigger asChild>
                    {asSpan ? (
                        <span
                            role="button"
                            tabIndex={0}
                            aria-label={label}
                            className={triggerClass}
                        >
                            <HelpCircle className="size-3.5" aria-hidden="true" />
                        </span>
                    ) : (
                        <button type="button" aria-label={label} className={triggerClass}>
                            <HelpCircle className="size-3.5" aria-hidden="true" />
                        </button>
                    )}
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content
                        side={side}
                        align="center"
                        sideOffset={6}
                        collisionPadding={10}
                        className="z-[80] max-w-xs rounded-lg border border-border bg-popover px-3 py-2 text-xs font-normal normal-case leading-relaxed tracking-normal text-popover-foreground shadow-lg"
                    >
                        {text}
                        <Tooltip.Arrow className="fill-popover" />
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>
        </Tooltip.Provider>
    );
}
